import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { trackPage } from "../../amplify/appsync/user-events";
import { AppAuthStateProps } from "../../types/propTypes";
import { getUserRole, UserRole } from "../../utils/user";
import { SystemBannerAdmin } from "../admin/SystemBannerAdmin";
import { Cohort } from "../cohort/Cohort";
import { Favorites } from "../cohort/Favorites";
import { SearchDashboard } from "../cohort/Search";
import { Trackboard } from "../cohort/Trackboard";
import { TrackboardAfib } from "../cohort/TrackboardAfib";
import { TrackboardCDU } from "../cohort/TrackboardCDU";
import TrackboardRedirect from "../cohort/TrackboardRedirect";
import { TrackboardV2 } from "../cohort/TrackboardV2";
import { Compass } from "../compass/Compass";
import { CompassAccrual } from "../compass/CompassAccrual";
import { CompassDashboard } from "../compass/CompassDashboard";
import CompassProspectiveDashboard from "../compass/CompassProspectiveDashboard";
import PrintPage from "../compass/PrintPage";
import { DashboardContainer } from "../dashboard/DashboardContainer";
import { AdminFeedbackReport } from "../dashboard/llm/feedback/AdminFeedbackReport";
import { MyDashboard } from "../dashboard/my/MyDashboard";
import { LLMVisit } from "../llm-visit/LLMVisit";
import { BoardingReport } from "../ops/BoardingReport";
import { EvaluationReport } from "../ops/EvaluationReport";
import { Metrics } from "../ops/Metrics";
import { Ops } from "../ops/Ops";
import { RevisitReport } from "../ops/RevisitReport";
import { ProfilePage } from "../profile/ProfilePage";
import { AdminUserEventsReport } from "../reports/user-events/AdminUserEventsReport";
import { NotFound } from "./NotFound";
import { ResponsiveAppBar } from "./ResponsiveAppBar";

interface NavigationProps extends AppAuthStateProps {
    assistantWidth: number;
}

export const Navigation = (props: NavigationProps) => {
    const location = useLocation();

    useEffect(() => {
        // Called every time the route changes
        trackPage(location, props.user?.username);
    }, [location]);

    return (
        <>
            <ResponsiveAppBar {...props} />
            <Routes>
                <Route path="/" element={
                    <MyDashboard {...props} />
                } />
                <Route path="/demo" element={
                    <Navigate to='/dashboard' />
                } />
                <Route path="/dashboard" element={
                    <Navigate to='/trackboard' />
                } />
                <Route path="/trackboard" element={<Trackboard {...props} />} />
                <Route path="/trackboard/:csn" element={
                    <TrackboardRedirect
                        {...props}
                    />
                } />
                <Route path="/trackboard/:csn/assessments" element={
                    <TrackboardRedirect
                        {...props}
                        subpage={"assessments"}
                    />
                } />
                <Route path="/trackboard-v2" element={<TrackboardV2 {...props} />} />
                <Route path="/favorites" element={<Favorites {...props} />} />
                <Route path="/search" element={<SearchDashboard {...props} />} />
                <Route path="/profile" element={<ProfilePage {...props} />} />

                {
                    getUserRole(props.user) === UserRole.ADMIN && (
                        <Route path="/dashboard/admin" element={
                            <DashboardContainer
                                {...props}
                            />
                        } />
                    )
                }
                <Route path="/dashboard/:internalCSN" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"notes"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/notes" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"notes"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/charting" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"charting"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/overview" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"overview"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/management" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"management"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/anatomy" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"anatomy"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/physiology" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"physiology"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/risk" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"risk"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/assessments" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"assessments"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/lidia" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"lidia"}
                    />
                } />
                <Route path="/dashboard/:internalCSN/evaluation" element={
                    <DashboardContainer
                        {...props}
                        initialKey={"evaluation"}
                    />
                } />
                <Route path="/llm-visit" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/explanation" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/explanation/:searchCSN" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/reasoning" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/reasoning/:searchCSN" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/admit" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/admit/:searchCSN" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/revisit" element={<LLMVisit {...props} />} />
                <Route path="/llm-visit/revisit/:searchCSN" element={<LLMVisit {...props} />} />
                {
                    getUserRole(props.user) === UserRole.ADMIN && (
                        <>
                            <Route path="/my" element={<MyDashboard {...props} />} />
                            <Route path="/llm-feedback" element={<AdminFeedbackReport {...props} />} />
                            <Route path="/user-events" element={<AdminUserEventsReport {...props} />} />
                            <Route path="/admin/system-banner" element={<SystemBannerAdmin />} />
                            <Route path="/ops" element={<Ops {...props} />} />
                            <Route path="/boarding" element={<BoardingReport {...props} />} />
                            <Route path="/evaluation" element={<EvaluationReport {...props} />} />
                            <Route path="/metrics" element={<Metrics {...props} />} />
                            <Route path="/cohort" element={<Cohort {...props} />} />
                            <Route path="/cdu" element={<TrackboardCDU {...props} />} />
                            <Route path="/revisit" element={<RevisitReport {...props} />} />
                            <Route path="/compass" element={<Compass {...props} />} />
                            <Route path="/compass/accrual" element={<CompassAccrual {...props} />} />
                            <Route path="/compass-prospective" element={<CompassProspectiveDashboard {...props} />} />
                            <Route path="/compass/:studyID" element={
                                <CompassDashboard
                                    {...props}
                                />
                            } />
                            <Route path="/print-report/:studyId" element={<PrintPage />} />
                            <Route path="/afib" element={<TrackboardAfib {...props} />} />
                        </>
                    )
                }
                <Route path="*" element={<NotFound />} />
            </Routes>
        </>
    );
};
